import { Head, useForm, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import type { PageProps } from "@/types/inertia"
import type { AdminSettings } from "@/types/inertia"
import { PATTERN_LABELS } from "@/editor-core/labels"
import type { PatternKind } from "@/editor-core/document"

const PATTERNS: PatternKind[] = ["straight", "brick", "third", "herringbone", "stagger"]

// One numeric knob: label, unit, and the policy sentence explaining what it
// binds. Every knob constrains the GENERATOR only — a hand-dragged seam may
// violate these and gets flagged, never blocked.
const KNOBS: { key: keyof AdminSettings; label: string; unit: string; hint: string }[] = [
  {
    key: "cut_margin_in",
    label: "Cut margin",
    unit: "in",
    hint: "Extra length added to every cut, for squaring the ends.",
  },
  {
    key: "cut_gap_in",
    label: "Cut gap",
    unit: "in",
    hint: "Spacing kept between cuts nested side-by-side across the roll's width, so a drifting rip cut can't ruin its neighbour.",
  },
  {
    key: "usable_width_trim_in",
    label: "Usable width trim",
    unit: "in",
    hint: "Subtracted from a roll's nominal width for all cut math — documents keep printing the nominal width.",
  },
  {
    key: "min_fill_in",
    label: "Minimum fill",
    unit: "in",
    hint: "Fill strips narrower than this are flagged in the editor (never blocked).",
  },
  {
    key: "remnant_min_length_ft",
    label: "Remnant threshold",
    unit: "ft",
    hint: "Leftover roll pieces at least this long count as remnants (reusable stock) instead of waste.",
  },
  {
    key: "roll_length_ft",
    label: "Roll length",
    unit: "ft",
    hint: "Default physical roll length the cut sheet nests cuts onto.",
  },
]

export default function AdminSettings({ settings }: { settings: AdminSettings }) {
  const errors = (usePage().props as unknown as PageProps).errors ?? {}
  const form = useForm({
    cut_margin_in: settings.cut_margin_in,
    cut_gap_in: settings.cut_gap_in,
    usable_width_trim_in: settings.usable_width_trim_in,
    min_fill_in: settings.min_fill_in,
    remnant_min_length_ft: settings.remnant_min_length_ft,
    roll_length_ft: settings.roll_length_ft,
    waste_suggestions: settings.waste_suggestions,
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    form.patch("/admin/settings", { preserveScroll: true })
  }

  return (
    <>
      <Head title="Settings">
        <meta name="description" content="Estimating policy: seam-planning rule knobs and waste suggestions." />
        <meta property="og:title" content="Settings" />
        <meta property="og:description" content="Estimating policy: seam-planning rule knobs and waste suggestions." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <h1>Settings</h1>
          <p className="mt-1">
            Company estimating policy. These rules drive proposed seam layouts and cut math — they
            never block a hand edit, only flag it.
          </p>
        </div>

        <form onSubmit={submit} className="mt-6 max-w-2xl space-y-8">
          <section>
            <h2>Seam planning rules</h2>
            <div className="mt-4 space-y-5">
              {KNOBS.map(({ key, label, unit, hint }) => (
                <div key={key} className="flex flex-col gap-1">
                  <label htmlFor={key}>{label}</label>
                  <div className="flex items-center gap-2">
                    <Input
                      id={key}
                      type="number"
                      className="w-28"
                      value={form.data[key] as number}
                      onChange={(e) => form.setData(key, Number(e.target.value))}
                      aria-invalid={!!errors[key]}
                    />
                    <span className="text-sm text-ink-muted">{unit}</span>
                  </div>
                  <p className="text-xs text-ink-muted">{hint}</p>
                  {errors[key] && <p className="text-xs text-danger-display">{errors[key]}</p>}
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2>Waste suggestions</h2>
            <p className="mt-1 text-sm text-ink-muted">
              Applied when a pattern is picked — the estimator can still override per room. A
              percentage added to net (&ldquo;+% of net&rdquo;).
            </p>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-5">
              {PATTERNS.map((pattern) => (
                <div key={pattern} className="flex flex-col gap-1">
                  <label htmlFor={`waste-${pattern}`}>{PATTERN_LABELS[pattern]}</label>
                  <div className="flex items-center gap-1">
                    <Input
                      id={`waste-${pattern}`}
                      type="number"
                      value={form.data.waste_suggestions[pattern]}
                      onChange={(e) =>
                        form.setData("waste_suggestions", {
                          ...form.data.waste_suggestions,
                          [pattern]: Number(e.target.value),
                        })
                      }
                    />
                    <span className="text-sm text-ink-muted">%</span>
                  </div>
                </div>
              ))}
            </div>
            {errors.waste_suggestions && (
              <p className="mt-2 text-xs text-danger-display">{errors.waste_suggestions}</p>
            )}
          </section>

          <div>
            <Button type="submit" disabled={form.processing}>
              Save settings
            </Button>
          </div>
        </form>
      </AdminShell>
    </>
  )
}
